import React, {
  useCallback,
} from 'react';
import {
  useNavigate,
} from 'react-router';
import {
  DateTime,
} from 'luxon';
import i18n from 'i18next';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getFeatures,
} from 'web-selectors';

import {
  useEntityDispatch,
  useProgramWorkout,
} from 'web-hooks';

import {
  Tabs,
  Video,
  Switch,
  MarkdownRenderer,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

import AdditionalWorkout from './AdditionalWorkout';
import WorkoutExercisesHeader from './WorkoutExercisesHeader';
import * as S from './styled';

type Props = {
  readOnly: boolean,
  activeTab: string,
  resourceType: 'workoutTemplates' | 'personalWorkouts',
  programWorkout: any,
  setTab: { (tab: string): void },
};

const ProgramWorkoutHeader = ({
  readOnly,
  activeTab,
  resourceType,
  programWorkout,
  setTab,
}: Props) => {
  const navigate = useNavigate();
  const workoutId = programWorkout.workout_id;
  const programId = programWorkout.program_id;
  const {
    saveWorkout,
    deleteWorkout,
    duplicateWorkout,
  } = useProgramWorkout({
    workoutId,
    programId,
    programWorkoutId: programWorkout.id,
    resourceType,
  });
  const {
    updateEntity,
  } = useEntityDispatch({
    id: workoutId,
    resourceType,
  });

  const mapState = useCallback(state => ({
    isPro: coreSelectors.getProfile('is_pro')(state),
    userId: coreSelectors.getProfile('id')(state),
    workout: coreSelectors.getResourceById(
      resourceType,
      workoutId,
    )(state),
    features: getFeatures(state),
  }), [workoutId, programId]);

  const {
    isPro,
    userId,
    workout,
    features,
  } = useSelector(mapState, shallowEqual);

  if (!workout?.id) return null;

  const dispatch = useDispatch();

  const handleEnter = () => {
    const now = DateTime.local().toISO();
    const onSuccess = (ev) => {
      navigate(`/events/${ev.id}/results`);
    };

    dispatch(
      coreActions.requestCreateWorkoutEvents({
        isNow: true,
        userId,
        payload: {
          begins_at: now,
          ends_at: now,
          personal_workout_id: workoutId,
        },
        callbacks: {
          onSuccess,
        },
      }),
    );
  };

  const actions = [
    {
      name: i18n.t('save.workout'),
      onClick: saveWorkout,
    },
    {
      name: 'Duplicate',
      onClick: duplicateWorkout,
    },
    {
      name: 'Delete',
      color: '#e54d42',
      onClick: deleteWorkout,
    },
  ];

  return (
    <Flex
      column
      width="100%"
      shrink="0"
      spacing={10}
      className="ProgramWorkoutHeader"
    >
      <WorkoutExercisesHeader
        name={workout.name}
        actions={actions}
        readOnly={readOnly}
        canSchedule={!isPro && features.results}
        onEnter={handleEnter}
        onSaveName={(name) => {
          updateEntity({ name });
        }}
      />
      <Flex padding="15px" width="100%">
        {!readOnly && features.die_roll && (
          <S.DieRoll>
            <span>Die Roll</span>
            <Switch
              checked={workout.has_die_roll}
              onChange={() => {
                updateEntity({ has_die_roll: !workout.has_die_roll });
              }}
            />
          </S.DieRoll>
        )}
        <Tabs
          active={activeTab}
          onRequestChange={tab => setTab(tab)}
        >
          <Flex
            width="100%"
            label="DESCRIPTION"
            value="description"
          >
            {readOnly ? (
              <MarkdownRenderer
                source={workout?.description}
              />
            ) : (
              <S.Description
                id={workout?.id}
                placeholder="Start typing a description here..."
                source={workout?.description}
                onBlur={() => {
                  // updateEntity({ description });
                }}
                onChange={(description) => {
                  updateEntity({ description });
                }}
              />
            )}
          </Flex>
          <Flex
            column
            width="30%"
            style={{
              minWidth: '400px',
            }}
            label="VIDEO"
            value="video"
          >
            <Video
              height="250px"
              id={workout.video_id}
              src={workout.video_url}
              title={workout.name}
              entityId={workoutId}
              isProgram
              readOnly={readOnly}
              resourceType={resourceType}
            />
          </Flex>
          <AdditionalWorkout
            label="WARMUP"
            value="warmup"
            id={workout.warmup_id}
            readOnly={readOnly}
            parentId={workoutId}
            parentType={resourceType}
            resourceType="warmups"
          />
          <AdditionalWorkout
            label="COOLDOWN"
            value="cooldown"
            id={workout.cooldown_id}
            readOnly={readOnly}
            parentId={workoutId}
            parentType={resourceType}
            resourceType="cooldowns"
          />
        </Tabs>
      </Flex>
    </Flex>
  );
};

export default ProgramWorkoutHeader;
